
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Base URL for your API
const BaseUrl = process.env.REACT_APP_API_URL;
// const BaseUrl = "http://192.168.88.203:5000";

// --- Asynchronous Thunks ---

// Add Wp Api Setup Thunk
export const AddDocumentLink = createAsyncThunk(
    'documentlink/AddDocumentLink',
    async ({ values }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BaseUrl}/wha_doclink_add`, values);
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Failed to add Official WA"
            );
        }
    }
);

// --- Async Thunk: Fetch All Document Links ---
export const fetchDocumentLinkAll = createAsyncThunk(
    'documentlink/fetchDocumentLinkAll',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/wha_doclink_view_All`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Failed to fetch Document Links'
            );
        }
    }
);

// --- Update Document Link Thunk ---
export const UpdateDocumentLink = createAsyncThunk(
    'documentlink/updateDocumentLink',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await axios.put(`${BaseUrl}/wha_doclink_update/${id}`, formData);
            return (id, { updatedData: formData }, response.data);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Failed to update document link'
            );
        }
    }
);


// --- Delete Document Link Thunk ---
export const DeleteDocumentLink = createAsyncThunk(
    'documentlink/deleteDocumentLink',
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/wha_doclink_delete/${id}`);
            return (id, response.data); // Return the ID for state update
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Failed to delete document link'
            );
        }
    }
);

// --- Add Document Link Detail Thunk ---
export const AddDocumentLinkDetail = createAsyncThunk(
    'documentlinkdetail/AddDocumentLinkDetail',
    async ({ values }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BaseUrl}/wha_doclinkdt_add/${values.ClientName}`, values,);
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Failed to add document link detail'
            );
        }
    }
);


export const fetchDocumentLinkDetail = createAsyncThunk(
    "documentlinkdetail/fetchDocumentLinkDetail",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/wha_doclinkdt_view_All`);
            if (Array.isArray(response.data)) {
                return response.data;
            } else if (Array.isArray(response.data.data)) {
                return response.data.data;
            } else {
                throw new Error("Unexpected response format");
            }
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Failed to fetch document link details"
            );
        }
    }
);

// Update Document Link Detail Thunk
export const UpdateDocumentLinkDetail = createAsyncThunk(
    'documentlinkdetail/UpdateDocumentLinkDetail',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                `${BaseUrl}/wha_doclinkdt_update/${id}`,
                {
                    WhaDocLinkID: formData.ClientName,        // <-- API expects this field
                    DocHeaderName: formData.DocHeaderName,
                    DocHeaderValue: formData.DocHeaderValue,
                }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to update document link detail"
            );
        }
    }
);


export const DeleteDocumentLinkDetail = createAsyncThunk(
    "documentlinkdetail/DeleteDocumentLinkDetail",
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/wha_doclinkdt_delete/${id}`);
            return (id, response.data); // Return the ID to remove from state
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Failed to delete item"
            );
        }
    }
);

// --- Redux Slice ---
const DocumentLinkSetup = createSlice({
    name: 'documentlinksetup',
    initialState: {

        // New State for Add Wp Api Setup
        AddDocumentLinkData: false,
        AddOfficialWALoading: false,
        AddOfficialWAError: null,

        // State for Document Link Data
        DocumentLinks: [],
        DocumentLinksLoading: false,
        DocumentLinksError: null,

        AddDocDetailData: false,
        AddDetailLoading: false,
        AddDetailError: null,

        DocumentLinkDetails: [],
        documentLinkDetailsLoading: false,
        documentLinkDetailsError: null,

        UpdateDocDetail: false,
        UpdateLoading: false,
        UpdateError: null,

        deleteDetailLoading: false,
        deleteDetailError: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder

            // ---- Add Wp Api Setup handler ----
            .addCase(AddDocumentLink.pending, (state) => {
                state.AddOfficialWALoading = true;
                state.AddOfficialWAError = null;
            })
            .addCase(AddDocumentLink.fulfilled, (state, action) => {
                state.AddOfficialWALoading = false;
                state.AddDocumentLinkData = action.payload;
            })
            .addCase(AddDocumentLink.rejected, (state, action) => {
                state.AddOfficialWALoading = false;
                state.AddOfficialWAError = action.payload;
            })

            // --- New fetchDocumentLinkAll handlers ---
            .addCase(fetchDocumentLinkAll.pending, (state) => {
                state.DocumentLinksLoading = true;
                state.DocumentLinksError = null;
            })
            .addCase(fetchDocumentLinkAll.fulfilled, (state, action) => {
                state.DocumentLinksLoading = false;
                state.DocumentLinks = action.payload;
            })
            .addCase(fetchDocumentLinkAll.rejected, (state, action) => {
                state.DocumentLinksLoading = false;
                state.DocumentLinksError = action.payload;
            })

            // --- Update Document Link handlers ---
            .addCase(UpdateDocumentLink.pending, (state) => {
                state.DocumentLinksLoading = true;
                state.DocumentLinksError = null;
            })
            .addCase(UpdateDocumentLink.fulfilled, (state, action) => {
                state.DocumentLinksLoading = true;
                const { id, updatedData } = action.payload;
                state.DocumentLinks = state.DocumentLinks.map(item =>
                    item._id === id ? { ...item, ...updatedData } : item
                );
            })
            .addCase(UpdateDocumentLink.rejected, (state, action) => {
                state.DocumentLinksLoading = true;
                state.DocumentLinksError = action.payload;
            })

            // --- Delete Document Link handlers ---
            .addCase(DeleteDocumentLink.fulfilled, (state, action) => {
                state.DocumentLinks = state.DocumentLinks.filter(item => item._id !== action.payload);
            })
            .addCase(DeleteDocumentLink.rejected, (state, action) => {
                state.DocumentLinksError = action.payload;
            })

            // ---- Add Doc link Detail Setup handler ----
            .addCase(AddDocumentLinkDetail.pending, (state) => {
                state.AddDetailLoading = true;
                state.AddDetailError = null;
                state.AddDetailSuccess = false;
            })
            .addCase(AddDocumentLinkDetail.fulfilled, (state, action) => {
                state.AddDetailLoading = false;
                state.AddDocumentLinkData = action.payload;
            })
            .addCase(AddDocumentLinkDetail.rejected, (state, action) => {
                state.AddDetailLoading = false;
                state.AddDetailError = action.payload;
            })

            // --- New fetch DocumentLink Detail All handlers ---
            .addCase(fetchDocumentLinkDetail.pending, (state) => {
                state.documentLinkDetailsLoading = true;
                state.documentLinkDetailsError = null;
            })
            .addCase(fetchDocumentLinkDetail.fulfilled, (state, action) => {
                state.documentLinkDetailsLoading = false;
                state.DocumentLinkDetails = action.payload;
            })
            .addCase(fetchDocumentLinkDetail.rejected, (state, action) => {
                state.documentLinkDetailsLoading = false;
                state.documentLinkDetailsError = action.payload;
            })

            .addCase(UpdateDocumentLinkDetail.pending, (state) => {
                state.UpdateLoading = true;
                state.UpdateError = null;
            })
            .addCase(UpdateDocumentLinkDetail.fulfilled, (state, action) => {
                state.UpdateLoading = false;
                state.UpdateDocDetail = action.payload;
            })
            .addCase(UpdateDocumentLinkDetail.rejected, (state, action) => {
                state.UpdateLoading = false;
                state.UpdateError = action.payload;
            })


            .addCase(DeleteDocumentLinkDetail.pending, (state) => {
                state.deleteDetailLoading = true;
                state.deleteDetailError = null;
            })
            .addCase(DeleteDocumentLinkDetail.fulfilled, (state, action) => {
                state.deleteDetailLoading = false;
                state.DocumentLinkDetails = state.DocumentLinkDetails.filter(
                    (item) => item._id !== action.payload
                );
            })
            .addCase(DeleteDocumentLinkDetail.rejected, (state, action) => {
                state.deleteDetailLoading = false;
                state.deleteDetailError = action.payload;
            });
    }
});

// Export the reducer
export default DocumentLinkSetup.reducer;